'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@sprd/sprd-component-kit/v2';
import { ReactNode, useMemo } from 'react';
import clsx from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import type { ProductTypeWithEmbroideryInformation } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/actions/productType';
import { AssortmentDetailSearchParams, assortmentDetailParamsSchema } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/utility/validationSchema';
import { pdpParamsWithSyncAtom } from '@/atoms/pdpParamsAtom';
import { volumeDiscountOpenDialogAtom } from '@/atoms/volumeDiscountDialogAtom';
import { ResponsiveDialog } from '@/components/ResponsiveDialog/ResponsiveDialog';
import { Locale } from '@/utility/locale';

// Dynamically imported so this heavier flow (form, print-type selection, share-link) isn't
// bundled into routes that never enable it (e.g. the marketplace, via embroideryInfo below).
const CalculateOfferDialog = dynamic(() => import('@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/CalculateOfferDialog/CalculateOfferDialog').then(mod => mod.CalculateOfferDialog));
export type VolumeDiscountDialogsProps = {
  discounts: ProductTypeDiscount[];
  productType: ClientProductType;
  currency: Currency;
  shopId: string;
  locale: Locale;
  amplienceMediaBaseUrl: string;
  isProduction: boolean;
  /** Enables the offer calculator ("Calculate volume discount") when provided, and carries the
   * shop it should price/submit embroidery against. Omit entirely where embroidery support isn't
   * known or the calculator isn't supported yet (e.g. the marketplace). */
  embroideryInfo?: Pick<ProductTypeWithEmbroideryInformation, 'isEmbroideryOnly' | 'isEmbroiderySupported'> & {
    /** Only CYO has a separate embroidery shop; pass `undefined` explicitly where the same shop
     * handles both print and embroidery. */
    shopId: string | undefined;
  };
};
const getMajorThresholds = (discounts: ProductTypeDiscount[], discountRows: number) => {
  if (discounts.length <= discountRows) {
    return discounts;
  }
  const lowest = discounts[0];
  const highest = discounts[discounts.length - 1];
  const step = Math.floor((discounts.length - 2) / (discountRows - 2));
  const intermediate = Array.from({
    length: discountRows - 2
  }, (_, i) => discounts[2 + i * step]);
  return [lowest, ...intermediate, highest];
};
export type VolumeDiscountTiersDialogProps = {
  discounts: ProductTypeDiscount[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rendered below the tier list, e.g. the ADP's offer-calculator button. */
  children?: ReactNode;
  /** Classes for the dialog box itself (it is portalled, so a wrapping element can't size it). */
  className?: string;
};

/** The volume-discount tier-list popup. Used by VolumeDiscountDialogs (MP PDP / CYO ADP) and by
 * the volume discount calculator's summary. */
export const VolumeDiscountTiersDialog = t0 => {
  const $ = _c(35);
  const {
    discounts,
    open,
    onOpenChange,
    children,
    className: t1
  } = t0;
  const className = t1 === undefined ? "pb-8 md:max-w-1/2 md:pb-0" : t1;
  const t = useTranslations();
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== discounts || $[2] !== onOpenChange || $[3] !== open || $[4] !== t) {
    const majorDiscounts = getMajorThresholds(discounts, 5);
    T0 = ResponsiveDialog;
    t4 = true;
    t5 = className;
    t6 = open;
    if ($[14] !== t) {
      t7 = t("cyoAssortmentDetailPage.volumeDiscount.title");
      $[14] = t;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    if ($[16] !== t) {
      t8 = t("general.close");
      $[16] = t;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t9 = onOpenChange;
    t3 = "flex flex-col gap-6 px-4 py-4 sm:px-6 md:px-8 lg:px-10";
    let t10;
    if ($[18] !== t) {
      t10 = discount => {
        if (!discount) {
          return null;
        }
        return <li key={discount.value} className={clsx("border-x border-t border-neutral-200 px-4 py-2 text-center text-sm font-normal", "last:border-b")}>{t("cyoAssortmentDetailPage.volumeDiscount.itemsAmount", {
            items: discount.from
          })}{" "}<span className="text-red-600">-{" "}{t("cyoAssortmentDetailPage.volumeDiscount.discountAmount", {
              discount: discount.value
            })}</span></li>;
      };
      $[18] = t;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    t2 = majorDiscounts.map(t10);
    $[0] = className;
    $[1] = discounts;
    $[2] = onOpenChange;
    $[3] = open;
    $[4] = t;
    $[5] = T0;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    T0 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t10;
  if ($[20] !== t2) {
    t10 = <ul>{t2}</ul>;
    $[20] = t2;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== children || $[23] !== t10 || $[24] !== t3) {
    t11 = <div className={t3}>{t10}{children}</div>;
    $[22] = children;
    $[23] = t10;
    $[24] = t3;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== T0 || $[27] !== t11 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t12 = <T0 dismissible={t4} className={t5} open={t6} title={t7} closeLabel={t8} onOpenChange={t9}>{t11}</T0>;
    $[26] = T0;
    $[27] = t11;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};
const getIsOfferShare = (searchParams: AssortmentDetailSearchParams) => searchParams?.appearance !== undefined && searchParams?.quantity !== undefined && searchParams?.views !== undefined && searchParams?.embroidery !== undefined;

/** Owns the volume-discount tier-list popup and the offer calculator dialog. Render this exactly
 * once per page — any number of VolumeDiscountActivator buttons (e.g. ADP's price-area link and
 * its shipping/exchange USP button) share it via volumeDiscountOpenDialogAtom, so there's only
 * ever one dialog in the tree, and a shared offer URL can only open it once. */
export const VolumeDiscountDialogs = t0 => {
  const $ = _c(36);
  const {
    discounts,
    productType,
    currency,
    shopId,
    locale,
    amplienceMediaBaseUrl,
    isProduction,
    embroideryInfo
  } = t0;
  const t = useTranslations();
  const rawSearchParams = useSearchParams();
  let t1;
  if ($[0] !== rawSearchParams) {
    const searchParamsObj = Object.fromEntries(rawSearchParams?.entries() ?? []);
    t1 = assortmentDetailParamsSchema.safeParse(searchParamsObj);
    $[0] = rawSearchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const searchParams = t1.data;
  const t2 = getIsOfferShare(searchParams) ? "offer-calculator" as const : undefined;
  let t3;
  if ($[2] !== t2) {
    t3 = [volumeDiscountOpenDialogAtom, t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = t3 as const;
  let t5;
  if ($[4] !== t4) {
    t5 = new Map([t4]);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useHydrateAtoms(t5);
  const [openDialog, setOpenDialog] = useAtom(volumeDiscountOpenDialogAtom);
  const pdpParams = useAtomValue(pdpParamsWithSyncAtom);
  const selectedAppearanceId = pdpParams?.appearanceId ?? productType.defaultValues.defaultAppearance.id;
  let t6;
  if ($[6] !== embroideryInfo || $[7] !== productType) {
    t6 = embroideryInfo && {
      ...productType,
      isEmbroideryOnly: embroideryInfo.isEmbroideryOnly,
      isEmbroiderySupported: embroideryInfo.isEmbroiderySupported
    };
    $[6] = embroideryInfo;
    $[7] = productType;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const productTypeWithEmbroideryInfo = t6;
  const t7 = openDialog === "volume-discount";
  let t8;
  if ($[9] !== setOpenDialog) {
    t8 = open => setOpenDialog(open ? "volume-discount" : undefined);
    $[9] = setOpenDialog;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== productTypeWithEmbroideryInfo || $[12] !== setOpenDialog || $[13] !== t) {
    t9 = productTypeWithEmbroideryInfo && <Button size="l" className="w-full self-center md:w-fit" onClick={() => setOpenDialog("offer-calculator")}>{t("cyoAssortmentDetailPage.volumeDiscount.calculateVolumeDiscount")}</Button>;
    $[11] = productTypeWithEmbroideryInfo;
    $[12] = setOpenDialog;
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== discounts || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t10 = <VolumeDiscountTiersDialog discounts={discounts} open={t7} onOpenChange={t8}>{t9}</VolumeDiscountTiersDialog>;
    $[15] = discounts;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== amplienceMediaBaseUrl || $[21] !== currency || $[22] !== discounts || $[23] !== embroideryInfo?.shopId || $[24] !== isProduction || $[25] !== locale || $[26] !== openDialog || $[27] !== productTypeWithEmbroideryInfo || $[28] !== searchParams || $[29] !== selectedAppearanceId || $[30] !== setOpenDialog || $[31] !== shopId) {
    t11 = productTypeWithEmbroideryInfo && <CalculateOfferDialog open={openDialog === "offer-calculator"} onOpenChange={open_0 => setOpenDialog(open_0 ? "offer-calculator" : undefined)} productType={productTypeWithEmbroideryInfo} selectedAppearanceId={selectedAppearanceId} currency={currency} shopId={shopId} embroideryShopId={embroideryInfo?.shopId} locale={locale} amplienceMediaBaseUrl={amplienceMediaBaseUrl} discounts={discounts} onDone={() => setOpenDialog(undefined)} searchParams={searchParams} isProduction={isProduction} />;
    $[20] = amplienceMediaBaseUrl;
    $[21] = currency;
    $[22] = discounts;
    $[23] = embroideryInfo?.shopId;
    $[24] = isProduction;
    $[25] = locale;
    $[26] = openDialog;
    $[27] = productTypeWithEmbroideryInfo;
    $[28] = searchParams;
    $[29] = selectedAppearanceId;
    $[30] = setOpenDialog;
    $[31] = shopId;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
};